import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useState, useEffect } from "react";
import {
  Plus, ExternalLink, Power, Trash2, CheckCircle2, XCircle, RefreshCw,
  Send, Calendar, BarChart3, AtSign, MessageSquare, Hash, TrendingUp,
  TrendingDown, Eye, Heart, MessageCircle, Share2, Clock, Globe, Shield,
  Zap, Users, ArrowUpRight, ChevronDown, Search, Filter, Bell, Star,
  ThumbsUp, ThumbsDown, Minus, Copy, Image, Video, Sparkles, X,
  Save, Gauge, AlertTriangle, ArrowUpDown
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from "recharts";

// ─── Constants ────────────────────────────────────────
const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "accounts", label: "Accounts", icon: Users },
  { id: "composer", label: "Composer", icon: Send },
  { id: "posts", label: "Posts", icon: Calendar },
  { id: "analytics", label: "Analytics", icon: TrendingUp },
  { id: "mentions", label: "Mentions", icon: AtSign },
  { id: "inbox", label: "Inbox", icon: MessageSquare },
];

const PLATFORM_CONFIG: Record<string, { color: string; bg: string; name: string; icon: string }> = {
  facebook: { color: "#1877F2", bg: "bg-blue-50", name: "Facebook", icon: "📘" },
  instagram: { color: "#E4405F", bg: "bg-pink-50", name: "Instagram", icon: "📸" },
  tiktok: { color: "#000000", bg: "bg-gray-50", name: "TikTok", icon: "🎵" },
  x: { color: "#1DA1F2", bg: "bg-sky-50", name: "X (Twitter)", icon: "𝕏" },
  linkedin: { color: "#0A66C2", bg: "bg-blue-50", name: "LinkedIn", icon: "💼" },
  youtube: { color: "#FF0000", bg: "bg-red-50", name: "YouTube", icon: "🎬" },
  pinterest: { color: "#E60023", bg: "bg-red-50", name: "Pinterest", icon: "📌" },
  google_business: { color: "#4285F4", bg: "bg-blue-50", name: "Google Business", icon: "📍" },
  threads: { color: "#000000", bg: "bg-gray-50", name: "Threads", icon: "🧵" },
  reddit: { color: "#FF4500", bg: "bg-orange-50", name: "Reddit", icon: "🤖" },
};

const SENTIMENT_COLORS = { positive: "#22C55E", neutral: "#94A3B8", negative: "#EF4444" };
const PUBLISH_NOW_SUPPORTED = new Set(["facebook", "instagram", "x", "linkedin", "youtube"]);
const MANUAL_ASSISTED_PLATFORMS = new Set(["tiktok", "google_business", "pinterest"]);

const fmt = (n: number) => n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n.toString();

function KPI({ label, value, icon: Icon, color, sub }: { label: string; value: string | number; icon: any; color: string; sub?: string }) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{typeof value === "number" ? fmt(value) : value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function Badge({ children, variant = "gray" }: { children: React.ReactNode; variant?: string }) {
  const colors: Record<string, string> = {
    green: "bg-green-100 text-green-700", red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700", yellow: "bg-yellow-100 text-yellow-700",
    purple: "bg-purple-100 text-purple-700", pink: "bg-pink-100 text-pink-700",
    gray: "bg-gray-100 text-gray-600", orange: "bg-orange-100 text-orange-700",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[variant] || colors.gray}`}>{children}</span>;
}

function PlatformBadge({ platform }: { platform: string }) {
  const p = PLATFORM_CONFIG[platform] || { name: platform, icon: "🌐", bg: "bg-gray-50" };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${p.bg}`}>{p.icon} {p.name}</span>;
}

function parseScopes(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((item) => String(item || "").trim()).filter(Boolean);
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((item) => String(item || "").trim()).filter(Boolean) : [];
  } catch {
    return raw.split(/[,\s]+/).map((item) => item.trim()).filter(Boolean);
  }
}

function hasAnyScope(scopes: string[], required: string[]) {
  const set = new Set(scopes);
  return required.some((scope) => set.has(scope));
}

function getPublishReadiness(account: any) {
  const platform = String(account?.platform || "").trim();
  const scopes = parseScopes(account?.scope);
  const tokenExpiresAt = account?.tokenExpiresAt ? new Date(account.tokenExpiresAt) : null;
  const tokenExpired = Boolean(tokenExpiresAt && !Number.isNaN(tokenExpiresAt.getTime()) && tokenExpiresAt.getTime() <= Date.now());

  if (!account?.isActive) {
    return { label: "Disabled", variant: "gray", reason: "This account is disabled and will not publish." };
  }

  if (tokenExpired) {
    return { label: "Reconnect", variant: "red", reason: "The saved token is expired. Reconnect this account before publishing." };
  }

  if (platform === "tiktok") {
    if (!hasAnyScope(scopes, ["video.publish", "video.upload"])) {
      return { label: "Sync Only", variant: "yellow", reason: "TikTok is connected for profile sync, but the current token does not include publishing scope." };
    }
    return {
      label: "Assisted Only",
      variant: "yellow",
      reason: "TikTok direct publish is still gated by TikTok policy. Use the manual publish pack unless the approved upload lane is cleared.",
    };
  }

  if (platform === "google_business") {
    return {
      label: "Assisted Only",
      variant: "yellow",
      reason: "Google Business is connected, but this standalone workspace uses an assisted publish lane for GBP content.",
    };
  }

  if (platform === "pinterest") {
    return {
      label: "Assisted Only",
      variant: "yellow",
      reason: "Pinterest is currently configured as an assisted/manual lane.",
    };
  }

  const rules: Record<string, { scopes: string[]; reason: string }> = {
    facebook: {
      scopes: ["pages_manage_posts"],
      reason: "Missing Facebook page publishing permission.",
    },
    instagram: {
      scopes: ["instagram_content_publish"],
      reason: "Missing Instagram content publish permission.",
    },
    x: {
      scopes: ["tweet.write"],
      reason: "Missing X posting permission.",
    },
    linkedin: {
      scopes: ["w_member_social"],
      reason: "Missing LinkedIn publishing permission.",
    },
    youtube: {
      scopes: ["https://www.googleapis.com/auth/youtube.upload", "youtube.upload"],
      reason: "Missing YouTube upload permission.",
    },
  };

  const rule = rules[platform];
  if (!rule) {
    return { label: "Connected", variant: "blue", reason: "This account is connected." };
  }

  if (!hasAnyScope(scopes, rule.scopes)) {
    return { label: "Sync Only", variant: "yellow", reason: rule.reason };
  }

  return { label: "Publish Ready", variant: "green", reason: "Publishing permissions are present." };
}

// ─── Main Component ───────────────────────────────────
export default function SocialHub() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");
  const [showConnect, setShowConnect] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [connectStep, setConnectStep] = useState<"select" | "connecting" | "success">("select");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [composerForm, setComposerForm] = useState({ accountIds: [] as string[], caption: "", hashtags: "", link: "", scheduledAt: "", mediaType: "image" });
  const [postFilter, setPostFilter] = useState("all");
  const [mentionFilter, setMentionFilter] = useState("all");
  const [inboxFilter, setInboxFilter] = useState("all");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [postSearch, setPostSearch] = useState("");
  const [postSortBy, setPostSortBy] = useState("createdAt");
  const [postSortDir, setPostSortDir] = useState<"asc" | "desc">("desc");
  const [viewName, setViewName] = useState("");
  const [viewDefault, setViewDefault] = useState(false);
  const [connectProgress, setConnectProgress] = useState(0);
  const [connectError, setConnectError] = useState("");
  const [toastMsg, setToastMsg] = useState("");

  // ─── Queries ───
  const { data: overview } = useQuery({ queryKey: ["social-overview"], queryFn: () => api.get<any>("/platforms/overview") });
  const { data: enterprise } = useQuery({ queryKey: ["social-enterprise"], queryFn: () => api.get<any>("/platforms/enterprise") });
  const { data: accounts } = useQuery({ queryKey: ["social-accounts"], queryFn: () => api.get<any[]>("/platforms/accounts") });
  const { data: available } = useQuery({ queryKey: ["social-available"], queryFn: () => api.get<any[]>("/platforms/available") });
  const { data: posts } = useQuery({
    queryKey: ["social-posts", postFilter, selectedAccount, postSearch, postSortBy, postSortDir],
    queryFn: () => api.get<any[]>(`/platforms/posts?limit=200${postFilter !== "all" ? `&status=${postFilter}` : ""}${selectedAccount ? `&accountId=${selectedAccount}` : ""}${postSearch ? `&search=${encodeURIComponent(postSearch)}` : ""}${postSortBy ? `&sortBy=${postSortBy}` : ""}${postSortDir ? `&sortDir=${postSortDir}` : ""}`),
  });
  const { data: growth } = useQuery({ queryKey: ["social-growth"], queryFn: () => api.get<any[]>("/platforms/growth-timeline?days=30") });
  const { data: mentions } = useQuery({ queryKey: ["social-mentions", mentionFilter], queryFn: () => api.get<any[]>(`/platforms/mentions${mentionFilter !== "all" ? `?sentiment=${mentionFilter}` : ""}`) });
  const { data: conversations } = useQuery({ queryKey: ["social-conversations", inboxFilter], queryFn: () => api.get<any[]>(`/platforms/conversations${inboxFilter !== "all" ? `?status=${inboxFilter}` : ""}`) });
  const { data: bestTimes } = useQuery({ queryKey: ["social-best-times"], queryFn: () => api.get<any>("/platforms/best-times") });
  const { data: hashtags } = useQuery({ queryKey: ["social-hashtags"], queryFn: () => api.get<any[]>("/platforms/hashtags") });
  const { data: socialViews } = useQuery({ queryKey: ["social-views"], queryFn: () => api.get<any>("/platforms/views") });
  const connectedPlatforms = new Set((accounts || []).map((account: any) => String(account.platform || "").trim()));
  const hasFacebookAccount = connectedPlatforms.has("facebook");
  const hasInstagramAccount = connectedPlatforms.has("instagram");

  // Toast auto-dismiss
  useEffect(() => { if (toastMsg) { const t = setTimeout(() => setToastMsg(""), 4000); return () => clearTimeout(t); } }, [toastMsg]);

  // ─── Mutations ───

  // OAuth popup message listener
  useEffect(() => {
    const handler = (ev: MessageEvent) => {
      if (ev.data?.type === "oauth-success") {
        setConnectProgress(100);
        setTimeout(() => {
          qc.invalidateQueries();
          setConnectStep("success");
          setConnectProgress(0);
          setConnectError("");
          setToastMsg("Account connected successfully!");
        }, 500);
      } else if (ev.data?.type === "oauth-error") {
        setConnectProgress(0);
        setConnectError(ev.data.error || "Connection failed. Please try again.");
        setConnectStep("select");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [qc]);

  const openOAuthPopup = (platform: string) => {
    setSelectedPlatform(platform);
    setConnectStep("connecting");
    setConnectProgress(15);
    setConnectError("");

    const w = 600, h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      `/api/platforms/oauth/redirect/${platform}`,
      "oauth_popup",
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
    );

    // Animate progress while popup is open
    let progress = 15;
    const interval = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(interval);
        // If we're still in "connecting" step, popup was closed without completing
        setConnectStep(prev => {
          if (prev === "connecting") {
            setConnectProgress(0);
            return "select";
          }
          return prev;
        });
        return;
      }
      if (progress < 85) {
        progress += Math.random() * 5 + 1;
        setConnectProgress(Math.min(85, progress));
      }
    }, 500);
  };

  const toggleMut = useMutation({
    mutationFn: (id: string) => api.patch(`/platforms/accounts/${id}/toggle`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-accounts"] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/platforms/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries(),
  });
  const refreshMut = useMutation({
    mutationFn: (id: string) => api.post(`/platforms/accounts/${id}/refresh`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social-accounts"] });
      qc.invalidateQueries({ queryKey: ["social-enterprise"] });
    },
  });
  const publishMut = useMutation({
    mutationFn: (id: string) => api.post(`/platforms/posts/${id}/publish`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social-posts"] });
      qc.invalidateQueries({ queryKey: ["social-overview"] });
      qc.invalidateQueries({ queryKey: ["social-enterprise"] });
      setToastMsg("Post published successfully.");
    },
    onError: (err: any) => {
      setToastMsg(err?.message || "Failed to publish post.");
    },
  });
  const createPostMut = useMutation({
    mutationFn: (body: any) => api.post("/platforms/posts/multi", body),
    onSuccess: () => {
      qc.invalidateQueries();
      qc.invalidateQueries({ queryKey: ["social-enterprise"] });
      setShowComposer(false);
    },
  });
  const deletePostMut = useMutation({
    mutationFn: (id: string) => api.delete(`/platforms/posts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social-posts"] });
      qc.invalidateQueries({ queryKey: ["social-enterprise"] });
    },
  });
  const markReadMut = useMutation({
    mutationFn: (id: string) => api.patch(`/platforms/mentions/${id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-mentions"] }),
  });
  const markAllReadMut = useMutation({
    mutationFn: () => api.post("/platforms/mentions/mark-all-read", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-mentions"] }),
  });
  const replyConvMut = useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) => api.post(`/platforms/conversations/${id}/reply`, { message }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-conversations"] }),
  });
  const updateConvMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.patch(`/platforms/conversations/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-conversations"] }),
  });
  const retryFailedMut = useMutation({
    mutationFn: async () => {
      const retryable = (posts || []).filter((p: any) => p.status === "failed" && canPublishNow(p));
      let success = 0;
      let failed = 0;
      for (const post of retryable) {
        try {
          await api.post(`/platforms/posts/${post.id}/publish`, {});
          success += 1;
        } catch {
          failed += 1;
        }
      }
      return { total: retryable.length, success, failed };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["social-posts"] });
      qc.invalidateQueries({ queryKey: ["social-overview"] });
      qc.invalidateQueries({ queryKey: ["social-enterprise"] });
      setToastMsg(`Retried ${result.success}/${result.total} failed posts${result.failed ? ` (${result.failed} still failed)` : ""}.`);
    },
  });
  const createViewMut = useMutation({
    mutationFn: (body: any) => api.post("/platforms/views", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social-views"] });
      setViewName("");
      setViewDefault(false);
    },
  });
  const updateViewMut = useMutation({
    mutationFn: ({ id, ...body }: any) => api.put(`/platforms/views/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-views"] }),
  });
  const deleteViewMut = useMutation({
    mutationFn: (id: string) => api.delete(`/platforms/views/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-views"] }),
  });

  const handleCompose = () => {
    if (!composerForm.accountIds.length || !composerForm.caption) return;
    createPostMut.mutate({
      accountIds: composerForm.accountIds,
      caption: composerForm.caption + (composerForm.hashtags ? `\n\n${composerForm.hashtags}` : ""),
      link: composerForm.link || undefined,
      mediaType: composerForm.mediaType,
      hashtags: composerForm.hashtags ? composerForm.hashtags.split(/\s+/).filter(Boolean) : undefined,
      scheduledAt: composerForm.scheduledAt || undefined,
    });
  };

  const hasMedia = (post: any) => {
    if (!post?.mediaUrls) return false;
    try {
      const parsed = typeof post.mediaUrls === "string" ? JSON.parse(post.mediaUrls) : post.mediaUrls;
      return Array.isArray(parsed) && parsed.length > 0;
    } catch {
      return false;
    }
  };

  const isYouTubeCommunityPost = (post: any) => {
    const mediaType = String(post?.mediaType || "").trim().toLowerCase();
    return post?.platform === "youtube" && (mediaType === "community" || mediaType === "post");
  };

  const canPublishNow = (post: any) => {
    if (!post?.platform) return false;
    if (!PUBLISH_NOW_SUPPORTED.has(post.platform)) return false;
    if (post.platform === "instagram" && !hasMedia(post)) return false;
    if (post.platform === "youtube" && isYouTubeCommunityPost(post)) return false;
    if (post.platform === "youtube" && !hasMedia(post)) return false;
    return true;
  };

  const publishUnavailableReason = (post: any) => {
    if (!post?.platform) return "Platform not detected.";
    if (!PUBLISH_NOW_SUPPORTED.has(post.platform)) {
      return `Publish Now is not available for ${PLATFORM_CONFIG[post.platform]?.name || post.platform} yet.`;
    }
    if (post.platform === "instagram" && !hasMedia(post)) {
      return "Instagram requires an image or video to publish.";
    }
    if (post.platform === "youtube" && isYouTubeCommunityPost(post)) {
      return "YouTube community posts are prepared here and completed through the assisted lane in YouTube Studio.";
    }
    if (post.platform === "youtube" && !hasMedia(post)) {
      return "YouTube Publish Now requires a video or image asset.";
    }
    return "Publish is currently unavailable for this post.";
  };

  const isManualLanePost = (post: any) =>
    MANUAL_ASSISTED_PLATFORMS.has(String(post?.platform || "").trim()) || isYouTubeCommunityPost(post);

  const getManualLaneReason = (post: any) => {
    const platform = String(post?.platform || "").trim();
    if (platform === "tiktok") {
      return "TikTok is currently using the assisted/manual lane because direct publish is being blocked by TikTok policy.";
    }
    if (platform === "google_business") {
      return "Google Business content is prepared here and published through the assisted lane.";
    }
    if (platform === "pinterest") {
      return "Pinterest is currently configured as an assisted lane.";
    }
    if (platform === "youtube" && isYouTubeCommunityPost(post)) {
      return "YouTube community posts are prepared here and then published manually through YouTube Studio Community.";
    }
    return "This post is using the assisted publish lane.";
  };

  const copyPostCaption = async (post: any) => {
    const parts = [String(post?.caption || "").trim(), String(post?.link || "").trim()].filter(Boolean);
    try {
      await navigator.clipboard.writeText(parts.join("\n\n"));
      setToastMsg("Post copy copied to clipboard.");
    } catch {
      setToastMsg("Could not copy post text automatically.");
    }
  };

  const openPostMediaAssets = (post: any) => {
    let urls: string[] = [];
    try {
      const parsed = typeof post?.mediaUrls === "string" ? JSON.parse(post.mediaUrls) : post?.mediaUrls;
      if (Array.isArray(parsed)) urls = parsed.map((item) => String(item || "").trim()).filter(Boolean);
    } catch {
      urls = [];
    }
    if (!urls.length) {
      setToastMsg("No media asset is attached to this post yet.");
      return;
    }
    urls.forEach((url) => window.open(url, "_blank", "noopener,noreferrer"));
    setToastMsg(`Opened ${urls.length} media asset${urls.length > 1 ? "s" : ""}.`);
  };

  const currentFilters = { postFilter, mentionFilter, inboxFilter, selectedAccount, postSearch, postSortBy, postSortDir };
  const applyView = (view: any) => {
    const f = view?.filters || {};
    setPostFilter(f.postFilter || "all");
    setMentionFilter(f.mentionFilter || "all");
    setInboxFilter(f.inboxFilter || "all");
    setSelectedAccount(f.selectedAccount || "");
    setPostSearch(f.postSearch || "");
    setPostSortBy(f.postSortBy || "createdAt");
    setPostSortDir(f.postSortDir === "asc" ? "asc" : "desc");
  };
  const isViewActive = (view: any) => {
    const f = view?.filters || {};
    return (
      (f.postFilter || "all") === postFilter &&
      (f.mentionFilter || "all") === mentionFilter &&
      (f.inboxFilter || "all") === inboxFilter &&
      (f.selectedAccount || "") === selectedAccount &&
      (f.postSearch || "") === postSearch &&
      (f.postSortBy || "createdAt") === postSortBy &&
      (f.postSortDir === "asc" ? "asc" : "desc") === postSortDir
    );
  };

  const postSignal = (post: any) => {
    if (post.status === "failed") return { label: "Publish failure", variant: "red" };
    if (post.status === "draft" && !post.scheduledAt) return { label: "Unscheduled", variant: "yellow" };
    if (post.status === "scheduled" && post.scheduledAt && new Date(post.scheduledAt).getTime() < Date.now()) {
      return { label: "Missed schedule", variant: "orange" };
    }
    return { label: "Healthy", variant: "green" };
  };

  // ─── Tab Content ──────────────────────────────────
  const renderTab = () => {
    switch (tab) {
      // ═══════════════════════════════════════════════
      // OVERVIEW TAB
      // ═══════════════════════════════════════════════
      case "overview":
        return (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
              <KPI label="Accounts" value={overview?.totalAccounts || 0} icon={Users} color="bg-violet-100 text-violet-600" sub={`${overview?.activeAccounts || 0} active`} />
              <KPI label="Followers" value={overview?.totalFollowers || 0} icon={Users} color="bg-blue-100 text-blue-600" />
              <KPI label="Posts" value={overview?.totalPosts || 0} icon={Send} color="bg-green-100 text-green-600" sub={`${overview?.scheduledPosts || 0} scheduled`} />
              <KPI label="Impressions" value={overview?.totalImpressions || 0} icon={Eye} color="bg-purple-100 text-purple-600" />
              <KPI label="Reach" value={overview?.totalReach || 0} icon={Globe} color="bg-pink-100 text-pink-600" />
              <KPI label="Engagement" value={`${overview?.avgEngagement || 0}%`} icon={Heart} color="bg-red-100 text-red-600" />
              <KPI label="Mentions" value={overview?.unreadMentions || 0} icon={AtSign} color="bg-yellow-100 text-yellow-600" sub="unread" />
              <KPI label="Inbox" value={overview?.openConversations || 0} icon={MessageSquare} color="bg-orange-100 text-orange-600" sub="open" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Gauge className="w-4 h-4 text-indigo-600" /> Command Score</h3>
                <p className="text-3xl font-bold text-gray-900 mt-2">{enterprise?.commandScore ?? 0}</p>
                <p className="text-xs text-gray-500 mt-1">Cross-channel social operations readiness</p>
              </div>
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">SLA Risk Radar</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between"><span className="text-gray-500">Responses over SLA</span><span className="font-semibold text-amber-600">{enterprise?.risks?.responseSlaBreaches ?? 0}</span></div>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Token expiry risk</span><span className="font-semibold text-red-600">{enterprise?.risks?.expiringTokens ?? 0}</span></div>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Publish failures (7d)</span><span className="font-semibold text-red-600">{enterprise?.risks?.failedPosts7d ?? 0}</span></div>
                </div>
              </div>
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Automation Footprint</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between"><span className="text-gray-500">Automation coverage</span><span className="font-semibold text-green-600">{enterprise?.performance?.automationCoveragePct ?? 0}%</span></div>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Publish reliability</span><span className="font-semibold text-indigo-600">{enterprise?.performance?.publishReliabilityPct ?? 0}%</span></div>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Response SLA</span><span className="font-semibold text-blue-600">{enterprise?.performance?.responseSlaPct ?? 0}%</span></div>
                </div>
              </div>
            </div>

            {/* Growth Chart + Platform Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 card p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Follower Growth (30 days)</h3>
                {growth?.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={growth}>
                      <defs>
                        <linearGradient id="sgFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => new Date(v).toLocaleDateString("en", { month: "short", day: "numeric" })} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
                      <Tooltip formatter={(v: any) => fmt(v)} labelFormatter={(l) => new Date(l).toLocaleDateString()} />
                      <Area type="monotone" dataKey="followers" stroke="#8B5CF6" fill="url(#sgFill)" strokeWidth={2} />
                      <Area type="monotone" dataKey="engagements" stroke="#EC4899" fill="none" strokeWidth={2} strokeDasharray="5 5" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-gray-400 py-8 text-center">No data yet</p>}
              </div>

              <div className="card p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Platform Breakdown</h3>
                <div className="space-y-3">
                  {overview?.platformBreakdown?.map((p: any) => {
                    const cfg = PLATFORM_CONFIG[p.platform];
                    return (
                      <div key={p.platform} className="flex items-center gap-3">
                        <span className="text-xl">{cfg?.icon || "🌐"}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between text-sm"><span className="font-medium">{cfg?.name}</span><span className="text-gray-500">{fmt(p.followers)}</span></div>
                          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                            <div className="h-1.5 rounded-full" style={{ width: `${Math.min((p.followers / (overview?.totalFollowers || 1)) * 100, 100)}%`, backgroundColor: cfg?.color || "#94A3B8" }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Sentiment */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Brand Sentiment</h4>
                  <div className="flex gap-3">
                    <div className="flex items-center gap-1 text-sm"><ThumbsUp className="w-3.5 h-3.5 text-green-500" /> <span className="font-medium">{overview?.sentiment?.positive || 0}</span></div>
                    <div className="flex items-center gap-1 text-sm"><Minus className="w-3.5 h-3.5 text-gray-400" /> <span className="font-medium">{overview?.sentiment?.neutral || 0}</span></div>
                    <div className="flex items-center gap-1 text-sm"><ThumbsDown className="w-3.5 h-3.5 text-red-500" /> <span className="font-medium">{overview?.sentiment?.negative || 0}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Posts + Top Posts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {overview?.recentPosts?.slice(0, 6).map((p: any) => (
                    <div key={p.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50">
                      <PlatformBadge platform={p.platform || ""} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 truncate">{p.caption}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <Badge variant={p.status === "published" ? "green" : p.status === "scheduled" ? "blue" : "gray"}>{p.status}</Badge>
                          {p.publishedAt && <span>{new Date(p.publishedAt).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      {p.status === "published" && (
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{fmt(p.likes)}</span>
                          <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{fmt(p.comments)}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="card p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Top Performing Posts</h3>
                <div className="space-y-3">
                  {overview?.topPosts?.map((p: any, i: number) => (
                    <div key={p.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 truncate">{p.caption?.substring(0, 80)}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <PlatformBadge platform={p.platform || ""} />
                          <span className="flex items-center gap-0.5"><Heart className="w-3 h-3 text-red-400" />{fmt(p.likes)}</span>
                          <span className="flex items-center gap-0.5"><Share2 className="w-3 h-3 text-blue-400" />{fmt(p.shares)}</span>
                          <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{fmt(p.impressions)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════
      // ACCOUNTS TAB
      // ═══════════════════════════════════════════════
      case "accounts":
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Connected Accounts ({accounts?.length || 0})</h2>
              <button onClick={() => { setShowConnect(true); setConnectStep("select"); }} className="btn-primary"><Plus className="w-4 h-4" /> Connect Account</button>
            </div>

            {accounts?.length ? (
              <>
                {hasFacebookAccount && !hasInstagramAccount && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Instagram is not restored in the standalone workspace yet.</p>
                        <p className="mt-1 text-amber-700">
                          Facebook is live, but no standalone Instagram business account is currently loaded. If Meta is unstable,
                          wait for Meta to recover and reconnect Instagram there instead of forcing a broken token cycle.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {accounts.map((acc: any) => {
                  const cfg = PLATFORM_CONFIG[acc.platform] || { name: acc.platform, icon: "🌐", bg: "bg-gray-50", color: "#94A3B8" };
                  const readiness = getPublishReadiness(acc);
                  return (
                    <div key={acc.id} className="card p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className={`w-14 h-14 rounded-2xl ${cfg.bg} flex items-center justify-center text-3xl`}>{cfg.icon}</div>
                            {acc.isVerified && <CheckCircle2 className="w-4 h-4 text-blue-500 absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{acc.accountName}</p>
                            <p className="text-xs text-gray-400">{cfg.name} · {acc.pageType}</p>
                          </div>
                        </div>
                        <div className={`w-2.5 h-2.5 rounded-full ${acc.isActive ? "bg-green-400" : "bg-gray-300"}`} title={acc.isActive ? "Active" : "Disabled"} />
                      </div>

                      {acc.bio && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{acc.bio}</p>}

                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant={readiness.variant}>{readiness.label}</Badge>
                        {acc.isVerified && <Badge variant="blue">Verified</Badge>}
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-2 mb-3 p-2 bg-gray-50 rounded-xl">
                        <div className="text-center"><p className="text-sm font-bold text-gray-900">{fmt(acc.followers)}</p><p className="text-[10px] text-gray-400">Followers</p></div>
                        <div className="text-center"><p className="text-sm font-bold text-gray-900">{fmt(acc.following)}</p><p className="text-[10px] text-gray-400">Following</p></div>
                        <div className="text-center"><p className="text-sm font-bold text-gray-900">{acc.engagementRate}%</p><p className="text-[10px] text-gray-400">Engagement</p></div>
                      </div>

                      <div className={`mb-3 rounded-xl border px-3 py-2 text-xs ${
                        readiness.variant === "green"
                          ? "border-green-200 bg-green-50 text-green-700"
                          : readiness.variant === "red"
                            ? "border-red-200 bg-red-50 text-red-700"
                            : readiness.variant === "yellow"
                              ? "border-amber-200 bg-amber-50 text-amber-700"
                              : "border-blue-200 bg-blue-50 text-blue-700"
                      }`}>
                        {readiness.reason}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                        <Shield className="w-3 h-3" />
                        <span>Connected via {acc.connectionMethod || "oauth"}</span>
                        {acc.tokenExpiresAt && <span className="ml-auto">Expires {new Date(acc.tokenExpiresAt).toLocaleDateString()}</span>}
                      </div>

                      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                        {acc.profileUrl && (
                          <a href={acc.profileUrl} target="_blank" rel="noopener" className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                            <ExternalLink className="w-3 h-3" /> Profile
                          </a>
                        )}
                        <button onClick={() => refreshMut.mutate(acc.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                          <RefreshCw className="w-3 h-3" /> Sync
                        </button>
                        <button onClick={() => toggleMut.mutate(acc.id)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${acc.isActive ? "bg-gray-100 text-gray-600 hover:bg-gray-200" : "bg-green-50 text-green-600 hover:bg-green-100"}`}>
                          <Power className="w-3 h-3" /> {acc.isActive ? "Disable" : "Enable"}
                        </button>
                        <button onClick={() => { if (confirm("Disconnect this account?")) deleteMut.mutate(acc.id); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors ml-auto">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </>
            ) : (
              <div className="card p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-pink-100 flex items-center justify-center text-3xl mx-auto mb-4">🔗</div>
                <p className="text-gray-500 mb-2">No accounts connected yet</p>
                <p className="text-xs text-gray-400 mb-4">Connect your social media accounts to start managing everything from one place</p>
                <button onClick={() => { setShowConnect(true); setConnectStep("select"); }} className="btn-primary">Connect Your First Account</button>
              </div>
            )}

            {/* Available Platforms */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Available Platforms</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {available?.map((p: any) => {
                  const cfg = PLATFORM_CONFIG[p.id] || { name: p.name, icon: "🌐", bg: "bg-gray-50", color: "#94A3B8" };
                  const connected = accounts?.some((a: any) => a.platform === p.id);
                  const needsMetaRestore = p.id === "instagram" && hasFacebookAccount && !hasInstagramAccount;
                  return (
                    <div key={p.id} className={`card p-4 text-center transition-all cursor-pointer ${connected ? "border-green-200 bg-green-50/30" : "hover:border-violet-200 hover:shadow-sm"}`}
                      onClick={() => { if (!connected) { setShowConnect(true); openOAuthPopup(p.id); } }}>
                      <div className={`w-14 h-14 rounded-2xl ${cfg.bg} flex items-center justify-center text-3xl mx-auto mb-2`}>{cfg.icon}</div>
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{p.features?.slice(0, 3).join(", ")}</p>
                      {connected ? <Badge variant="green">Connected</Badge> : (
                        <span className={`text-xs font-medium mt-1 inline-block ${needsMetaRestore ? "text-amber-600" : "text-violet-600"}`}>
                          {needsMetaRestore ? "Reconnect after Meta recovers" : "Sign in to Connect"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════
      // COMPOSER TAB
      // ═══════════════════════════════════════════════
      case "composer":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Compose Area */}
              <div className="lg:col-span-2 space-y-4">
                <div className="card p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Create Post</h3>

                  {/* Platform Selection */}
                  <div className="mb-4">
                    <label className="label">Publish to:</label>
                    <div className="flex flex-wrap gap-2">
                      {accounts?.filter((a: any) => a.isActive).map((a: any) => {
                        const cfg = PLATFORM_CONFIG[a.platform] || { icon: "🌐", name: a.platform };
                        const selected = composerForm.accountIds.includes(a.id);
                        return (
                          <button key={a.id}
                            onClick={() => setComposerForm(f => ({ ...f, accountIds: selected ? f.accountIds.filter(id => id !== a.id) : [...f.accountIds, a.id] }))}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border-2 transition-all ${selected ? "border-violet-400 bg-violet-50 text-violet-700" : "border-gray-200 hover:border-gray-300"}`}>
                            <span>{cfg.icon}</span> {a.accountName}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Caption */}
                  <div className="mb-4">
                    <label className="label">Caption / Text</label>
                    <textarea className="input min-h-[120px]" placeholder="What's on your mind? Write your post caption here..."
                      value={composerForm.caption} onChange={e => setComposerForm(f => ({ ...f, caption: e.target.value }))} />
                    <div className="flex justify-between mt-1">
                      <p className="text-xs text-gray-400">{composerForm.caption.length} characters</p>
                      {composerForm.accountIds.length > 0 && (
                        <p className="text-xs text-gray-400">Posting to {composerForm.accountIds.length} account{composerForm.accountIds.length > 1 ? "s" : ""}</p>
                      )}
                    </div>
                  </div>

                  {/* Hashtags */}
                  <div className="mb-4">
                    <label className="label">Hashtags</label>
                    <input className="input" placeholder="#marketing #socialmedia #growth"
                      value={composerForm.hashtags} onChange={e => setComposerForm(f => ({ ...f, hashtags: e.target.value }))} />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="label">Link (optional)</label>
                      <input className="input" placeholder="https://..." value={composerForm.link} onChange={e => setComposerForm(f => ({ ...f, link: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Media Type</label>
                      <select className="input" value={composerForm.mediaType} onChange={e => setComposerForm(f => ({ ...f, mediaType: e.target.value }))}>
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                        <option value="carousel">Carousel</option>
                        <option value="reel">Reel / Short</option>
                        <option value="community">Community / Post</option>
                        <option value="story">Story</option>
                      </select>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="mb-4">
                    <label className="label">Schedule (optional)</label>
                    <input type="datetime-local" className="input" value={composerForm.scheduledAt} onChange={e => setComposerForm(f => ({ ...f, scheduledAt: e.target.value }))} />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button onClick={handleCompose} disabled={!composerForm.accountIds.length || !composerForm.caption || createPostMut.isPending}
                      className="btn-primary flex items-center gap-2">
                      {composerForm.scheduledAt ? <Clock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                      {createPostMut.isPending ? "Posting..." : composerForm.scheduledAt ? "Schedule Post" : "Create Draft"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Sidebar — Best Times + Top Hashtags */}
              <div className="space-y-4">
                <div className="card p-5">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-violet-500" /> Best Posting Times</h3>
                  {bestTimes?.bestHours?.slice(0, 6).map((h: any, i: number) => (
                    <div key={h.hour} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${i < 3 ? "bg-gradient-to-br from-violet-500 to-pink-500 text-white" : "bg-gray-100 text-gray-500"}`}>{i + 1}</span>
                        <span className="text-sm font-medium">{h.label}</span>
                      </div>
                      <span className="text-xs text-gray-400">{h.avgEngagement} avg eng.</span>
                    </div>
                  ))}
                  {bestTimes?.bestDays?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-2">Best Days</p>
                      <div className="flex flex-wrap gap-1.5">
                        {bestTimes.bestDays.slice(0, 3).map((d: any) => (
                          <Badge key={d.day} variant="purple">{d.name}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="card p-5">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Hash className="w-4 h-4 text-pink-500" /> Top Hashtags</h3>
                  <div className="space-y-2">
                    {hashtags?.slice(0, 8).map((h: any) => (
                      <div key={h.hashtag} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-violet-600">{h.hashtag}</span>
                        <div className="text-xs text-gray-400"><span className="font-medium text-gray-600">{fmt(h.totalEngagement)}</span> eng · {h.usageCount}x</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════
      // POSTS TAB
      // ═══════════════════════════════════════════════
      case "posts":
        const postStatusCounts = { all: posts?.length || 0, published: posts?.filter((p: any) => p.status === "published").length || 0, scheduled: posts?.filter((p: any) => p.status === "scheduled").length || 0, draft: posts?.filter((p: any) => p.status === "draft").length || 0 };
        const retryableFailedCount = posts?.filter((p: any) => p.status === "failed" && canPublishNow(p)).length || 0;
        const filteredPosts = postFilter === "all" ? posts : posts?.filter((p: any) => p.status === postFilter);
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="card p-3">
                <p className="text-xs text-gray-500 flex items-center gap-1"><Gauge className="w-3.5 h-3.5 text-indigo-600" /> Command Score</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{enterprise?.commandScore ?? 0}</p>
              </div>
              <div className="card p-3">
                <p className="text-xs text-gray-500">Response SLA Breaches</p>
                <p className="text-xl font-bold text-amber-600 mt-1">{enterprise?.risks?.responseSlaBreaches ?? 0}</p>
              </div>
              <div className="card p-3">
                <p className="text-xs text-gray-500">Publish Reliability</p>
                <p className="text-xl font-bold text-green-600 mt-1">{enterprise?.performance?.publishReliabilityPct ?? 0}%</p>
              </div>
              <div className="card p-3">
                <p className="text-xs text-gray-500">Token Expiry Risk</p>
                <p className="text-xl font-bold text-red-600 mt-1">{enterprise?.risks?.expiringTokens ?? 0}</p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              {(["all", "published", "scheduled", "draft"] as const).map(s => (
                <button key={s} onClick={() => setPostFilter(s)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${postFilter === s ? "bg-gradient-to-r from-violet-500 to-pink-500 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)} ({postStatusCounts[s]})
                </button>
              ))}
              {retryableFailedCount > 0 && (
                <button
                  onClick={() => retryFailedMut.mutate()}
                  disabled={retryFailedMut.isPending}
                  className="btn-secondary ml-auto"
                >
                  {retryFailedMut.isPending ? "Retrying..." : `Retry Failed (${retryableFailedCount})`}
                </button>
              )}
              <button onClick={() => { setTab("composer"); }} className={`btn-primary ${retryableFailedCount > 0 ? "" : "ml-auto"}`}><Plus className="w-4 h-4" /> New Post</button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-full md:w-[260px]">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                <input className="input pl-9" placeholder="Search caption, hashtag, link..." value={postSearch} onChange={(e) => setPostSearch(e.target.value)} />
              </div>
              <select className="input w-full md:w-[220px]" value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}>
                <option value="">All Accounts</option>
                {accounts?.map((a: any) => (
                  <option key={a.id} value={a.id}>{a.accountName} ({PLATFORM_CONFIG[a.platform]?.name || a.platform})</option>
                ))}
              </select>
              <select className="input w-full md:w-[200px]" value={postSortBy} onChange={(e) => setPostSortBy(e.target.value)}>
                <option value="createdAt">Sort: Created</option>
                <option value="updatedAt">Sort: Updated</option>
                <option value="scheduledAt">Sort: Scheduled</option>
                <option value="publishedAt">Sort: Published</option>
                <option value="engagementRate">Sort: Engagement</option>
                <option value="impressions">Sort: Impressions</option>
              </select>
              <button className="btn-secondary flex items-center gap-1" onClick={() => setPostSortDir(postSortDir === "desc" ? "asc" : "desc")}>
                <ArrowUpDown className="w-4 h-4" /> {postSortDir === "desc" ? "Desc" : "Asc"}
              </button>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Save className="w-4 h-4 text-purple-600" /> Saved Views</h3>
                <input className="input h-9 max-w-[220px]" placeholder="View name" value={viewName} onChange={(e) => setViewName(e.target.value)} />
                <label className="text-xs text-gray-600 flex items-center gap-1"><input type="checkbox" checked={viewDefault} onChange={(e) => setViewDefault(e.target.checked)} />Default</label>
                <button
                  className="btn-secondary h-9"
                  onClick={() => {
                    const name = viewName.trim();
                    if (!name) return;
                    createViewMut.mutate({ name, filters: currentFilters, isDefault: viewDefault });
                  }}
                  disabled={!viewName.trim() || createViewMut.isPending}
                >
                  Save View
                </button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {socialViews?.views?.length ? socialViews.views.map((v: any) => (
                  <div key={v.id} className={`px-3 py-2 rounded-xl border text-xs ${isViewActive(v) ? "border-violet-300 bg-violet-50" : "border-gray-200 bg-white"}`}>
                    <button className="font-medium text-gray-700" onClick={() => applyView(v)}>{v.name}</button>
                    <div className="flex gap-2 mt-1">
                      {!v.isDefault && <button className="text-gray-500 hover:text-violet-600" onClick={() => updateViewMut.mutate({ id: v.id, isDefault: true })}>Default</button>}
                      <button className="text-red-500 hover:text-red-600" onClick={() => deleteViewMut.mutate(v.id)}>Delete</button>
                    </div>
                  </div>
                )) : <p className="text-xs text-gray-400">No saved views yet.</p>}
              </div>
            </div>

            {/* Post Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredPosts?.map((p: any) => (
                <div key={p.id} className="card p-4 hover:shadow-md transition-shadow">
                  {(() => {
                    const signal = postSignal(p);
                    return (
                      <div className="mb-2">
                        <Badge variant={signal.variant}>{signal.label}</Badge>
                      </div>
                    );
                  })()}
                  <div className="flex items-center justify-between mb-2">
                    <PlatformBadge platform={p.platform || ""} />
                    <Badge variant={p.status === "published" ? "green" : p.status === "scheduled" ? "blue" : "gray"}>{p.status}</Badge>
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-3 mb-3">{p.caption}</p>
                  {p.status === "failed" && p.errorMessage && (
                    <p className="text-xs text-red-500 line-clamp-2 mb-2">{p.errorMessage}</p>
                  )}
                  {p.status === "draft" && p.errorMessage && (
                    <p className="text-xs text-amber-600 line-clamp-2 mb-2">{p.errorMessage}</p>
                  )}
                  {isManualLanePost(p) && (
                    <p className="text-xs text-amber-600 line-clamp-2 mb-2">{getManualLaneReason(p)}</p>
                  )}
                  {p.status === "draft" && p.platform === "tiktok" && !p.errorMessage && (
                    <p className="text-xs text-amber-600 line-clamp-2 mb-2">TikTok is manual publish right now. Use Publish from draft when ready.</p>
                  )}
                  {p.mediaType && (
                    <div className="flex items-center gap-1 mb-2">
                      {p.mediaType === "video" || p.mediaType === "reel" ? <Video className="w-3.5 h-3.5 text-gray-400" /> : <Image className="w-3.5 h-3.5 text-gray-400" />}
                      <span className="text-xs text-gray-400 capitalize">{p.mediaType}</span>
                    </div>
                  )}
                  {p.status === "published" && (
                    <div className="grid grid-cols-4 gap-2 p-2 bg-gray-50 rounded-lg mb-2">
                      <div className="text-center"><p className="text-xs font-bold">{fmt(p.impressions)}</p><p className="text-[9px] text-gray-400">Impr.</p></div>
                      <div className="text-center"><p className="text-xs font-bold">{fmt(p.likes)}</p><p className="text-[9px] text-gray-400">Likes</p></div>
                      <div className="text-center"><p className="text-xs font-bold">{fmt(p.comments)}</p><p className="text-[9px] text-gray-400">Comments</p></div>
                      <div className="text-center"><p className="text-xs font-bold">{fmt(p.shares)}</p><p className="text-[9px] text-gray-400">Shares</p></div>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
                    <span>{p.publishedAt ? new Date(p.publishedAt).toLocaleDateString() : p.scheduledAt ? `Scheduled: ${new Date(p.scheduledAt).toLocaleDateString()}` : "Draft"}</span>
                    <div className="flex gap-1">
                      {p.status === "draft" && canPublishNow(p) && (
                        <button
                          onClick={() => publishMut.mutate(p.id)}
                          disabled={publishMut.isPending}
                          className="text-xs text-violet-600 font-medium hover:text-violet-700 disabled:opacity-50"
                        >
                          {publishMut.isPending ? "Publishing..." : "Publish"}
                        </button>
                      )}
                      {p.status === "scheduled" && canPublishNow(p) && (
                        <button
                          onClick={() => publishMut.mutate(p.id)}
                          disabled={publishMut.isPending}
                          className="text-xs text-green-600 font-medium hover:text-green-700 disabled:opacity-50"
                        >
                          {publishMut.isPending ? "Publishing..." : "Publish Now"}
                        </button>
                      )}
                      {p.status === "failed" && canPublishNow(p) && (
                        <button
                          onClick={() => publishMut.mutate(p.id)}
                          disabled={publishMut.isPending}
                          className="text-xs text-amber-600 font-medium hover:text-amber-700 disabled:opacity-50"
                        >
                          {publishMut.isPending ? "Retrying..." : "Retry Now"}
                        </button>
                      )}
                      {isManualLanePost(p) && (
                        <>
                          <button onClick={() => copyPostCaption(p)} className="text-xs text-indigo-600 font-medium hover:text-indigo-700">
                            Copy Caption
                          </button>
                          <button onClick={() => openPostMediaAssets(p)} className="text-xs text-sky-600 font-medium hover:text-sky-700">
                            Open Asset
                          </button>
                        </>
                      )}
                      <button onClick={() => { if (confirm("Delete this post?")) deletePostMut.mutate(p.id); }} className="text-xs text-red-500 hover:text-red-600 ml-2">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════
      // ANALYTICS TAB
      // ═══════════════════════════════════════════════
      case "analytics":
        return (
          <div className="space-y-6">
            {/* Engagement Overview */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <KPI label="Total Likes" value={overview?.totalLikes || 0} icon={Heart} color="bg-red-100 text-red-600" />
              <KPI label="Comments" value={overview?.totalComments || 0} icon={MessageCircle} color="bg-blue-100 text-blue-600" />
              <KPI label="Shares" value={overview?.totalShares || 0} icon={Share2} color="bg-green-100 text-green-600" />
              <KPI label="Impressions" value={overview?.totalImpressions || 0} icon={Eye} color="bg-purple-100 text-purple-600" />
              <KPI label="Avg Engagement" value={`${overview?.avgEngagement || 0}%`} icon={Zap} color="bg-yellow-100 text-yellow-600" />
            </div>

            {/* Engagement Timeline */}
            <div className="card p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Engagement Over Time</h3>
              {growth?.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={growth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => new Date(v).toLocaleDateString("en", { month: "short", day: "numeric" })} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
                    <Tooltip formatter={(v: any) => fmt(v)} labelFormatter={(l) => new Date(l).toLocaleDateString()} />
                    <Legend />
                    <Bar dataKey="engagements" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="impressions" fill="#EC4899" radius={[4, 4, 0, 0]} opacity={0.6} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-gray-400 py-8 text-center">No data yet</p>}
            </div>

            {/* Per-Platform Performance + Hashtag Performance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Performance by Platform</h3>
                {overview?.platformBreakdown?.length ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={overview.platformBreakdown} dataKey="followers" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {overview.platformBreakdown.map((p: any, i: number) => <Cell key={i} fill={p.color || "#8B5CF6"} />)}
                      </Pie>
                      <Tooltip formatter={(v: any) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-gray-400 py-8 text-center">No data</p>}
              </div>

              <div className="card p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Hashtag Performance</h3>
                <div className="space-y-3">
                  {hashtags?.slice(0, 8).map((h: any) => (
                    <div key={h.hashtag}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-violet-600">{h.hashtag}</span>
                        <span className="text-gray-500">{fmt(h.totalImpressions)} impr.</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="h-2 rounded-full bg-gradient-to-r from-violet-500 to-pink-500" style={{ width: `${Math.min((h.totalEngagement / (hashtags?.[0]?.totalEngagement || 1)) * 100, 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Best Times Heatmap */}
            <div className="card p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Best Posting Times</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {bestTimes?.bestHours?.slice(0, 12).map((h: any, i: number) => (
                  <div key={h.hour} className={`p-3 rounded-xl text-center ${i < 3 ? "bg-gradient-to-br from-violet-50 to-pink-50 border border-violet-200" : "bg-gray-50"}`}>
                    <p className="text-lg font-bold text-gray-900">{h.label}</p>
                    <p className="text-xs text-gray-500">{h.avgEngagement} avg eng.</p>
                    <p className="text-[10px] text-gray-400">{h.posts} posts</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════
      // MENTIONS TAB
      // ═══════════════════════════════════════════════
      case "mentions":
        const unread = mentions?.filter((m: any) => !m.isRead).length || 0;
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {["all", "positive", "neutral", "negative"].map(s => (
                <button key={s} onClick={() => setMentionFilter(s)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${mentionFilter === s ? "bg-gradient-to-r from-violet-500 to-pink-500 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
              {unread > 0 && (
                <button onClick={() => markAllReadMut.mutate()} className="ml-auto text-sm text-violet-600 font-medium hover:text-violet-700">
                  Mark All Read ({unread})
                </button>
              )}
            </div>

            <div className="space-y-3">
              {mentions?.map((m: any) => (
                <div key={m.id} className={`card p-4 ${!m.isRead ? "border-l-4 border-l-violet-400" : ""} hover:shadow-sm transition-shadow`}>
                  <div className="flex items-start gap-3">
                    <img src={m.authorAvatar || `https://ui-avatars.com/api/?name=${m.authorName}`} className="w-10 h-10 rounded-full" alt="" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{m.authorName}</span>
                        <span className="text-xs text-gray-400">{m.authorHandle}</span>
                        <PlatformBadge platform={m.platform} />
                        <Badge variant={m.sentiment === "positive" ? "green" : m.sentiment === "negative" ? "red" : "gray"}>{m.sentiment}</Badge>
                        <Badge variant={m.type === "mention" ? "purple" : m.type === "tag" ? "blue" : "gray"}>{m.type}</Badge>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{m.content}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span>{new Date(m.mentionedAt).toLocaleDateString()}</span>
                        <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{fmt(m.reach)} reach</span>
                        <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{fmt(m.engagements)} eng</span>
                        {m.postUrl && <a href={m.postUrl} target="_blank" rel="noopener" className="text-violet-600 hover:text-violet-700 flex items-center gap-0.5"><ExternalLink className="w-3 h-3" />View</a>}
                        {!m.isRead && <button onClick={() => markReadMut.mutate(m.id)} className="text-violet-600 hover:text-violet-700">Mark Read</button>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════
      // INBOX TAB
      // ═══════════════════════════════════════════════
      case "inbox":
        const openCount = conversations?.filter((c: any) => c.status === "open").length || 0;
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {["all", "open", "replied", "closed"].map(s => (
                <button key={s} onClick={() => setInboxFilter(s)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${inboxFilter === s ? "bg-gradient-to-r from-violet-500 to-pink-500 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
              <span className="ml-auto text-sm text-gray-500">{openCount} open conversations</span>
            </div>

            <div className="space-y-3">
              {conversations?.map((c: any) => (
                <div key={c.id} className={`card p-4 ${!c.isRead ? "border-l-4 border-l-violet-400" : ""} hover:shadow-sm transition-shadow`}>
                  <div className="flex items-start gap-3">
                    <img src={c.authorAvatar || `https://ui-avatars.com/api/?name=${c.authorName}`} className="w-10 h-10 rounded-full" alt="" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-sm">{c.authorName}</span>
                        <span className="text-xs text-gray-400">{c.authorHandle}</span>
                        <PlatformBadge platform={c.platform} />
                        <Badge variant={c.status === "open" ? "blue" : c.status === "replied" ? "green" : c.status === "closed" ? "gray" : "yellow"}>{c.status}</Badge>
                        <Badge variant={c.priority === "urgent" ? "red" : c.priority === "high" ? "orange" : "gray"}>{c.priority}</Badge>
                        <Badge variant={c.type === "dm" ? "purple" : c.type === "comment" ? "blue" : "gray"}>{c.type}</Badge>
                      </div>
                      {c.subject && <p className="text-sm font-medium text-gray-900 mb-1">{c.subject}</p>}
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{c.lastMessage}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span>{new Date(c.lastMessageAt).toLocaleDateString()}</span>
                        <span>{c.messageCount} messages</span>
                        <div className="flex items-center gap-1 ml-auto">
                          {c.status === "open" && (
                            <button onClick={() => {
                              const msg = prompt("Type your reply:");
                              if (msg) replyConvMut.mutate({ id: c.id, message: msg });
                            }} className="px-3 py-1 rounded-lg text-xs font-medium bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors">Reply</button>
                          )}
                          {c.status !== "closed" && (
                            <button onClick={() => updateConvMut.mutate({ id: c.id, data: { status: "closed" } })} className="px-3 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">Close</button>
                          )}
                          {c.status === "closed" && (
                            <button onClick={() => updateConvMut.mutate({ id: c.id, data: { status: "open" } })} className="px-3 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-600 hover:bg-green-100 transition-colors">Reopen</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Hub</h1>
          <p className="text-sm text-gray-500 mt-1">Manage all your social media accounts, posts, and analytics in one place</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setShowConnect(true); setConnectStep("select"); }} className="btn-primary">
            <Plus className="w-4 h-4" /> Connect Account
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
            {t.id === "mentions" && (overview?.unreadMentions || 0) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{overview?.unreadMentions}</span>
            )}
            {t.id === "inbox" && (overview?.openConversations || 0) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-violet-500 text-white text-[10px] font-bold">{overview?.openConversations}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {renderTab()}

      {/* ═══════════════════════════════════════════════ */}
      {/* CONNECT MODAL — Real OAuth Popup Flow           */}
      {/* ═══════════════════════════════════════════════ */}
      {showConnect && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowConnect(false); setConnectStep("select"); setConnectError(""); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 rounded-t-2xl p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">
                  {connectStep === "select" ? "Connect Social Account" : connectStep === "connecting" ? `Connecting ${PLATFORM_CONFIG[selectedPlatform]?.name || selectedPlatform}...` : "Account Connected!"}
                </h2>
                <button onClick={() => { setShowConnect(false); setConnectStep("select"); setConnectError(""); }} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              {connectStep === "connecting" && (
                <p className="text-sm text-white/80 mt-1">Complete the login in the popup window</p>
              )}
            </div>

            <div className="p-6">
              {/* Step 1: Select Platform */}
              {connectStep === "select" && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500 mb-3">Choose a platform to connect. You'll be redirected to log in and authorize access.</p>

                  {/* Error Display */}
                  {connectError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                      <XCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{connectError}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {available?.map((p: any) => {
                      const cfg = PLATFORM_CONFIG[p.id] || { icon: "🌐", bg: "bg-gray-50", name: p.name };
                      const connected = accounts?.some((a: any) => a.platform === p.id);
                      return (
                        <button key={p.id} disabled={connected}
                          onClick={() => openOAuthPopup(p.id)}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${connected ? "border-green-200 bg-green-50/50 opacity-60 cursor-not-allowed" : "border-gray-200 hover:border-violet-300 hover:bg-violet-50/30 cursor-pointer"}`}>
                          <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center text-xl`}>{cfg.icon}</div>
                          <div>
                            <p className="text-sm font-medium">{cfg.name}</p>
                            <p className="text-[10px] text-gray-400">{connected ? "Already connected" : "Click to connect"}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 2: Connecting (OAuth popup is open) */}
              {connectStep === "connecting" && (
                <div className="space-y-4 text-center py-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl mb-2 text-left">
                    <div className={`w-12 h-12 rounded-xl ${PLATFORM_CONFIG[selectedPlatform]?.bg || "bg-gray-100"} flex items-center justify-center text-2xl`}>
                      {PLATFORM_CONFIG[selectedPlatform]?.icon || "🌐"}
                    </div>
                    <div>
                      <p className="font-semibold">{PLATFORM_CONFIG[selectedPlatform]?.name}</p>
                      <p className="text-xs text-gray-500">OAuth 2.0 Authorization</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-violet-500 to-pink-500 rounded-full transition-all duration-500 ease-out" style={{ width: `${connectProgress}%` }} />
                    </div>
                    <p className="text-xs text-gray-500 animate-pulse">
                      {connectProgress < 30 ? "Opening " + (PLATFORM_CONFIG[selectedPlatform]?.name || "platform") + " login..." :
                       connectProgress < 60 ? "Waiting for authorization..." :
                       connectProgress < 90 ? "Processing authorization..." : "Finalizing connection..."}
                    </p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-blue-700 font-medium">Secure OAuth 2.0 Login</span>
                    </div>
                    <p className="text-xs text-blue-600">A popup window should have opened. Please complete your login there. If it didn't open, check your popup blocker.</p>
                  </div>

                  <div className="flex justify-between pt-2">
                    <button onClick={() => { setConnectStep("select"); setConnectProgress(0); setConnectError(""); }} className="btn-secondary">Cancel</button>
                    <button onClick={() => openOAuthPopup(selectedPlatform)}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-white bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 hover:from-violet-600 hover:via-purple-600 hover:to-pink-600 shadow-lg shadow-purple-200">
                      <RefreshCw className="w-4 h-4" />
                      Retry
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Success */}
              {connectStep === "success" && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 animate-bounce">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Account Connected!</h3>
                  <p className="text-sm text-gray-500 mb-4">Your {PLATFORM_CONFIG[selectedPlatform]?.name} account has been successfully connected and is ready to use.</p>
                  <div className="flex justify-center gap-3">
                    <button onClick={() => { setShowConnect(false); setConnectStep("select"); setTab("accounts"); }} className="btn-primary">View Accounts</button>
                    <button onClick={() => { setConnectStep("select"); setConnectError(""); }} className="btn-secondary">Connect Another</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMsg && (
        <div className={`fixed bottom-6 right-6 z-[60] px-5 py-3 rounded-xl shadow-2xl text-sm font-medium text-white animate-slide-up flex items-center gap-2 ${
          toastMsg.includes("failed") || toastMsg.includes("Failed") ? "bg-red-500" : "bg-gradient-to-r from-violet-500 to-pink-500"
        }`}>
          {toastMsg.includes("failed") || toastMsg.includes("Failed") ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          {toastMsg}
        </div>
      )}
    </div>
  );
}
